'use client'

import { useState } from 'react'
import { X, Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import clsx from 'clsx'

import AssetPickerDialog from '@/components/AssetPickerDialog'
import InstagramIcon from '@/components/InstagramIcon'
import LinkedInIcon from '@/components/LinkedInIcon'
import TikTokIcon from '@/components/TikTokIcon'
import FacebookIcon from '@/components/FacebookIcon'
import { Button } from '@/components/ui/button'
import { api } from '@/utils/trpc'
import { SocialPostPlatform } from '@/server/db/schema'

const ALL_PLATFORMS: SocialPostPlatform[] = [
  'INSTAGRAM',
  'LINKEDIN',
  'TIKTOK',
  'FACEBOOK',
]

function StagingThumbnail({
  assetId,
  onRemove,
  removeLabel,
}: {
  assetId: string
  onRemove: () => void
  removeLabel: string
}) {
  const query = api.assets.get.useQuery({ id: assetId })
  const url = query.data?.thumbnailUrl ?? query.data?.url ?? null

  return (
    <div className="relative size-14 shrink-0 rounded-xl overflow-hidden bg-input/40 ring-1 ring-foreground/10">
      {url ? (
        <img
          src={url}
          alt=""
          className="absolute inset-0 size-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 animate-pulse bg-input/60" />
      )}
      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        className="absolute top-0.5 right-0.5 flex size-4 items-center justify-center rounded-full bg-black/70 text-white transition-opacity hover:opacity-80"
      >
        <X className="size-2.5" />
      </button>
    </div>
  )
}

const PLATFORM_CONFIG: {
  platform: SocialPostPlatform
  icon: React.ComponentType<{ className?: string }>
  labelKey:
    | 'platformInstagram'
    | 'platformLinkedIn'
    | 'platformTikTok'
    | 'platformFacebook'
}[] = [
  { platform: 'INSTAGRAM', icon: InstagramIcon, labelKey: 'platformInstagram' },
  { platform: 'LINKEDIN', icon: LinkedInIcon, labelKey: 'platformLinkedIn' },
  { platform: 'TIKTOK', icon: TikTokIcon, labelKey: 'platformTikTok' },
  { platform: 'FACEBOOK', icon: FacebookIcon, labelKey: 'platformFacebook' },
]

type PostsGenerationFormProps = {
  className?: string
}

export default function PostsGenerationForm({
  className = '',
}: PostsGenerationFormProps) {
  const t = useTranslations('PostsGenerationForm')
  const utils = api.useUtils()

  const [assetIds, setAssetIds] = useState<string[]>([])
  const [platforms, setPlatforms] =
    useState<SocialPostPlatform[]>(ALL_PLATFORMS)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [queued, setQueued] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutation = api.instagramPosts.enqueue.useMutation({
    onSuccess: () => {
      setAssetIds([])
      setQueued(true)
      utils.instagramPosts.list.invalidate()
      setError(null)
      setTimeout(() => setQueued(false), 2500)
    },
    onError: (err: { message?: string }) => {
      setError(err.message ?? 'Ein Fehler ist aufgetreten.')
    },
  })

  const handleConfirmAssets = (ids: string[]) => {
    setAssetIds((prev) => {
      const merged = [...prev]
      for (const id of ids) if (!merged.includes(id)) merged.push(id)
      return merged
    })
    setQueued(false)
    setError(null)
  }

  const handleRemove = (id: string) => {
    setAssetIds((prev) => prev.filter((x) => x !== id))
  }

  const togglePlatform = (p: SocialPostPlatform) => {
    setPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    )
  }

  const handleEnqueue = () => {
    if (assetIds.length === 0 || platforms.length === 0 || mutation.isPending)
      return
    setError(null)
    const items = assetIds.flatMap((assetId) =>
      platforms.map((platform) => ({ assetId, platform })),
    )
    mutation.mutate({ items })
  }

  return (
    <div className={clsx('flex flex-col gap-6', className)}>
      <header className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
          {t('heading')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('subheading')}</p>
      </header>

      {/* Asset staging area */}
      <div className="flex flex-col gap-3">
        {assetIds.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {assetIds.map((id) => (
              <StagingThumbnail
                key={id}
                assetId={id}
                onRemove={() => handleRemove(id)}
                removeLabel={t('removeFromStaging')}
              />
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={mutation.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-foreground/15 bg-input/20 px-4 py-4 text-sm text-muted-foreground transition-all hover:border-foreground/30 hover:bg-input/40 disabled:opacity-50"
        >
          <Plus className="size-4" />
          {assetIds.length === 0 ? t('selectAsset') : t('addAnotherAsset')}
        </button>
      </div>

      {/* Platform chips */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">{t('platformsLabel')}</span>
        <div className="flex flex-wrap gap-2">
          {PLATFORM_CONFIG.map(({ platform, icon: Icon, labelKey }) => {
            const active = platforms.includes(platform)
            return (
              <button
                key={platform}
                type="button"
                onClick={() => togglePlatform(platform)}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ring-1 transition-all',
                  active
                    ? 'bg-foreground text-background ring-foreground'
                    : 'bg-input/30 text-muted-foreground ring-foreground/10 hover:ring-foreground/30',
                )}
              >
                <Icon className="size-3.5" />
                {t(labelKey)}
              </button>
            )
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        {error && <span className="text-sm text-destructive">{error}</span>}
        {queued && (
          <span className="text-sm text-green-500">
            {t('queued', { count: 1 })}
          </span>
        )}
        <Button
          type="button"
          onClick={handleEnqueue}
          disabled={
            assetIds.length === 0 ||
            platforms.length === 0 ||
            mutation.isPending
          }
          className="w-full sm:w-auto"
        >
          {mutation.isPending
            ? t('generating')
            : t('enqueue', { count: assetIds.length })}
        </Button>
      </div>

      <AssetPickerDialog
        open={pickerOpen}
        onOpenChangeAction={(open: boolean) => setPickerOpen(open)}
        multiple
        initialSelectedIds={assetIds}
        onConfirmManyAction={handleConfirmAssets}
        types={['GENERATION', 'VIDEO']}
      />
    </div>
  )
}
